import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Add, DeleteOutline, People, Refresh } from '@mui/icons-material';
import {
	Alert, Autocomplete, Box, Button,
	IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { AgentClient, AuditClient, AuditRecordModel, SearchAuditRequest, UserClient } from '@@api';
import { useApiClient, useHasPermission, useIsMobile } from '@@common';
import { GalleryHeader } from '@@components';
import { Permissions } from '../../common/Permissions';
import AgentIcon from '../../common/components/AgentIcon';
import useDebouncedCallback from '../../common/hooks/useDebouncedCallback';
import AuditFilterAutocomplete from './AuditFilterAutocomplete';
import AuditDetailDialog, { formatAuditTimestamp } from './AuditDetailDialog';

type IdentityOption = { id: string; kind: 'User' | 'Agent'; label: string };
type AttributeFilter = { key: string; value: string };
const pageSize = 50;
const filterDebounceMs = 300;
const filterMinWidth = 220;
const firstPage: Array<string | undefined> = [undefined];

export default function AuditView() {
	const client = useApiClient(AuditClient);
	const userClient = useApiClient(UserClient);
	const agentClient = useApiClient(AgentClient);
	const canReadUsers = useHasPermission(Permissions.UserRead);
	const canReadAgents = useHasPermission(Permissions.AgentRead);
	const isMobile = useIsMobile();
	const [source, setSource] = useState('');
	const [type, setType] = useState('');
	const [actionName, setActionName] = useState('');
	const [timestampFrom, setTimestampFrom] = useState('');
	const [timestampTo, setTimestampTo] = useState('');
	const [identity, setIdentity] = useState<IdentityOption | null>(null);
	const [identityInput, setIdentityInput] = useState('');
	const [identitySearch, setIdentitySearch] = useState('');
	const [identityOpen, setIdentityOpen] = useState(false);
	const [attributes, setAttributes] = useState<AttributeFilter[]>([]);
	const [filters, setFilters] = useState<SearchAuditRequest | null>(null);
	const [pageTokens, setPageTokens] = useState(firstPage);
	const [selectedRecord, setSelectedRecord] = useState<AuditRecordModel | null>(null);

	const updateIdentitySearch = useDebouncedCallback((value: string) => setIdentitySearch(value.trim()), filterDebounceMs);
	const updateFilters = useDebouncedCallback((next: SearchAuditRequest | null | undefined) => {
		if (next !== undefined) {
			setFilters(next);
			setPageTokens(firstPage);
		}
	}, filterDebounceMs);

	const users = useQuery({
		queryKey: ['audit', 'users', identitySearch],
		queryFn: ({ signal }) => userClient.getUsers(0, 25, identitySearch || undefined, signal),
		enabled: identityOpen && canReadUsers,
	});
	const agents = useQuery({
		queryKey: ['audit', 'agents', identitySearch],
		queryFn: ({ signal }) => agentClient.getAgents(0, 25, identitySearch || undefined, signal),
		enabled: identityOpen && canReadAgents,
	});

	const identities: IdentityOption[] = [
		...(canReadUsers ? users.data?.items ?? [] : []).map((user): IdentityOption => ({
			id: user.id, kind: 'User', label: user.displayName ? `${user.displayName} (${user.email})` : user.email,
		})),
		...(canReadAgents ? agents.data?.items ?? [] : []).map((agent): IdentityOption => ({
			id: agent.id, kind: 'Agent', label: agent.name,
		})),
	];
	if (identity && !identities.some((option) => option.id === identity.id && option.kind === identity.kind)) {
		identities.push(identity);
	}

	const token = pageTokens[pageTokens.length - 1];
	const audit = useQuery({
		queryKey: ['audit', 'records', filters, token],
		queryFn: ({ signal }) => filters
			? client.search({ ...filters, continuationToken: token }, signal)
			: client.browse(pageSize, token, signal),
	});
	const rows = audit.data?.items ?? [];
	const attributeKeys = attributes.filter(({ key, value }) => key || value).map(({ key }) => key);
	const invalidAttributes = attributeKeys.includes('') || new Set(attributeKeys).size !== attributeKeys.length;
	const fromUtc = timestampFrom ? Date.parse(`${timestampFrom}Z`) : undefined;
	const toUtc = timestampTo ? Date.parse(`${timestampTo}Z`) : undefined;
	const invalidTimestampRange = Number.isNaN(fromUtc) || Number.isNaN(toUtc)
		|| (fromUtc !== undefined && toUtc !== undefined && fromUtc > toUtc);

	useEffect(() => {
		if (invalidAttributes || invalidTimestampRange) {
			updateFilters(undefined);
			return;
		}

		const attributeFilters = attributes.filter(({ key }) => key);
		updateFilters(source || type || actionName || identity || attributeFilters.length
			|| fromUtc !== undefined || toUtc !== undefined ? {
			source: source || undefined,
			type: type || undefined,
			actionName: actionName || undefined,
			identityId: identity?.id,
			timestampFrom: fromUtc === undefined ? undefined : new Date(fromUtc).toISOString(),
			timestampTo: toUtc === undefined ? undefined : new Date(toUtc).toISOString(),
			attributes: Object.fromEntries(attributeFilters.map(({ key, value }) => [key, value])),
			pageSize,
		} : null);
	}, [actionName, attributes, fromUtc, identity, invalidAttributes, invalidTimestampRange, source, toUtc, type, updateFilters]);

	const clearFilters = () => {
		setSource('');
		setType('');
		setActionName('');
		setTimestampFrom('');
		setTimestampTo('');
		setIdentity(null);
		setIdentityInput('');
		updateIdentitySearch('');
		setAttributes([]);
	};

	const updateAttribute = (index: number, field: keyof AttributeFilter, value: string) => {
		setAttributes((current) => current.map((item, position) => position === index ? { ...item, [field]: value } : item));
	};

	const columns: GridColDef<AuditRecordModel>[] = [
		{ field: 'timestamp', headerName: 'Timestamp (UTC)', minWidth: 205, valueFormatter: (value: string) => formatAuditTimestamp(value) },
		{ field: 'type', headerName: 'Type', minWidth: 120, flex: 1 },
		{ field: 'actionName', headerName: 'Action', minWidth: 160, flex: 1 },
		{ field: 'source', headerName: 'Source', minWidth: 130, flex: 1, valueFormatter: (value) => value ?? '—' },
		{ field: 'identityName', headerName: 'Identity', minWidth: 160, flex: 1 },
		{
			field: 'details', headerName: 'Details', width: 100,
			renderCell: ({ row }) => <Button size="small" onClick={() => setSelectedRecord(row)}>View</Button>,
		},
	];

	return (
		<Stack spacing={2} sx={{ flexGrow: 1, minHeight: 0, minWidth: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'auto' }}>
			<GalleryHeader
				title="Audit"
				subtitle="Browse audit records for the selected tenant."
				utilityActions={<Tooltip title="Refresh records"><span><IconButton aria-label="Refresh records" disabled={audit.isFetching} onClick={() => void audit.refetch()}><Refresh /></IconButton></span></Tooltip>}
			/>

			<Paper sx={{ p: 2, containerType: 'inline-size' }}>
				<Stack spacing={1.5}>
					<Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${filterMinWidth}px), 1fr))` }}>
						<TextField label="Timestamp from (UTC)" type="datetime-local" value={timestampFrom} onChange={(event) => setTimestampFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
						<TextField label="Timestamp to (UTC)" type="datetime-local" value={timestampTo} onChange={(event) => setTimestampTo(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
						<Autocomplete
							sx={(theme) => ({
								[`@container (min-width: calc(${2 * filterMinWidth}px + ${theme.spacing(1.5)}))`]: {
									gridColumn: 'span 2',
								},
							})}
							options={identities}
							value={identity}
							inputValue={identityInput}
							open={identityOpen}
							onOpen={() => setIdentityOpen(true)}
							onClose={() => setIdentityOpen(false)}
							onChange={(_, option) => setIdentity(option)}
							onInputChange={(_, value) => { setIdentityInput(value); updateIdentitySearch(value); }}
							getOptionLabel={(option) => `${option.label} (${option.kind})`}
							getOptionKey={(option) => `${option.kind}:${option.id}`}
							isOptionEqualToValue={(option, value) => option.id === value.id && option.kind === value.kind}
							renderOption={({ key, ...props }, option) => {
								return (
									<Box component="li" key={key} {...props} sx={{ gap: 1 }}>
										<Box aria-hidden="true" sx={{ display: 'flex', flexShrink: 0, fontSize: 20, color: 'text.secondary', '& > svg': { fontSize: 'inherit' } }}>
											{option.kind === 'User' ? <People /> : <AgentIcon />}
										</Box>
										<Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{option.label}</Typography>
										<Typography variant="caption" color="text.secondary">{option.kind}</Typography>
									</Box>
								);
							}}
							filterOptions={(options) => options}
							loading={users.isFetching || agents.isFetching}
							disabled={!canReadUsers && !canReadAgents}
							renderInput={(params) => <TextField {...params} label="Identity" placeholder="Search users or agents" helperText={users.isError || agents.isError ? 'Some identities could not be loaded.' : undefined} />}
						/>
						<AuditFilterAutocomplete field="source" label="Source" value={source} onChange={setSource} />
						<AuditFilterAutocomplete field="type" label="Type" value={type} onChange={setType} />
						<AuditFilterAutocomplete field="action" label="Action" value={actionName} onChange={setActionName} />
					</Box>
					{invalidTimestampRange && <Typography variant="caption" color="error">Enter a valid range with the start at or before the end.</Typography>}
					<Box>
						<Button startIcon={<Add />} onClick={() => setAttributes((current) => [...current, { key: '', value: '' }])}>Add attribute</Button>
					</Box>
					{attributes.map(({ key, value }, index) => (
						<Stack key={index} direction="row" spacing={1} alignItems="center">
							<AuditFilterAutocomplete field="attributeKey" size="small" label={`Attribute key ${index + 1}`} value={key} onChange={(selected) => updateAttribute(index, 'key', selected)} />
							<TextField size="small" label={`Attribute value ${index + 1}`} value={value} onChange={(event) => updateAttribute(index, 'value', event.target.value)} fullWidth />
							<IconButton aria-label={`Remove attribute ${index + 1}`} onClick={() => setAttributes((current) => current.filter((_, position) => position !== index))}><DeleteOutline /></IconButton>
						</Stack>
					))}
					{invalidAttributes && <Typography variant="caption" color="error">Enter a unique key for each attribute.</Typography>}
					<Box>
						<Button onClick={clearFilters}>Clear filters</Button>
					</Box>
				</Stack>
			</Paper>

			{audit.isError && <Alert severity="error" action={<Button color="inherit" onClick={() => void audit.refetch()}>Retry</Button>}>Audit records could not be loaded.</Alert>}
			<Paper sx={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<DataGrid rows={rows} columns={columns} loading={audit.isFetching} hideFooter disableColumnFilter disableColumnMenu disableColumnSorting disableRowSelectionOnClick density={isMobile ? 'compact' : 'standard'} sx={{ border: 0 }} />
				<Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ p: 1 }}>
					<Typography variant="body2">Page {pageTokens.length}</Typography>
					<Button disabled={pageTokens.length <= 1 || audit.isFetching} onClick={() => setPageTokens((current) => current.slice(0, -1))}>Previous</Button>
					<Button disabled={!audit.data?.continuationToken || audit.isFetching} onClick={() => {
						const nextToken = audit.data?.continuationToken;
						if (nextToken) setPageTokens((current) => [...current, nextToken]);
					}}>Next</Button>
				</Stack>
			</Paper>

			<AuditDetailDialog record={selectedRecord} onClose={() => setSelectedRecord(null)} />
		</Stack>
	);
}
